import React from 'react';
import { Input } from './ui/input';

/**
 * Compact per-step scheduling controls: time-of-day + weekday chips.
 * Interpreted in the user's timezone (set on their profile).
 *
 * Props:
 *   value: { send_time_of_day: 'HH:MM'|null, send_days: number[]|null }
 *   onChange: (patch) => void  // partial update, merged into step
 */
const DOW = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

export default function StepScheduleControls({ value = {}, onChange, timezone }) {
  const tod = value.send_time_of_day || '';
  const days = Array.isArray(value.send_days) ? value.send_days : [];
  const setDay = (n) => {
    const nd = days.includes(n) ? days.filter(x => x !== n) : [...days, n].sort((a,b)=>a-b);
    onChange({ send_days: nd.length === 0 ? null : nd });
  };
  const clearAll = () => onChange({ send_days: null, send_time_of_day: null });
  const anyActive = tod || days.length > 0;

  return (
    <div className="mt-3 rounded-md border border-zinc-800 bg-[#0a0a0b] p-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] uppercase tracking-wider text-zinc-500">Send window</span>
        <span className="text-[10px] text-zinc-600">timezone: <span className="text-zinc-400">{timezone || 'UTC'}</span></span>
        {anyActive && (
          <button type="button" onClick={clearAll} className="ml-auto text-[10px] text-zinc-500 hover:text-red-400" data-testid="schedule-clear">Clear</button>
        )}
      </div>
      <div className="mt-2 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-xs text-zinc-500">Time of day</label>
          <Input
            type="time"
            value={tod}
            onChange={e => onChange({ send_time_of_day: e.target.value || null })}
            className="w-[110px] h-8 bg-[#111110] border-zinc-800 text-zinc-100 focus-visible:ring-[#d4a94a] text-xs"
            data-testid="schedule-time"
          />
          {!tod && <span className="text-[10px] text-zinc-600">any time</span>}
        </div>
        <div className="flex items-center gap-1.5">
          <label className="text-xs text-zinc-500 mr-1">Weekdays</label>
          {DOW.map((label, i) => {
            const active = days.includes(i);
            return (
              <button
                key={label}
                type="button"
                onClick={() => setDay(i)}
                data-testid={`schedule-day-${i}`}
                className={`h-7 min-w-[34px] px-2 rounded-md text-[11px] font-medium border transition-colors ${
                  active
                    ? 'bg-[#d4a94a] text-black border-[#d4a94a]'
                    : 'bg-transparent text-zinc-400 border-zinc-800 hover:border-[#3a2f18] hover:text-[#f5d67a]'
                }`}
              >{label}</button>
            );
          })}
          {days.length === 0 && <span className="text-[10px] text-zinc-600 ml-1">any day</span>}
        </div>
      </div>
    </div>
  );
}
